import Link from "next/link";
import { ArrowRight, Camera, ImageOff, RotateCcw } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface InvalidImageResultsProps {
  invalidReason?: string;
  recommendations: Array<string>;
  productTypeSlug: string;
  onReset: () => void;
}

const PHOTO_TIPS = [
  "Take clear, well-lit photos of the actual products",
  "Include several angles so the condition can be judged",
  "Avoid logos, screenshots, and catalog images",
] as const;

export function InvalidImageResults({
  invalidReason,
  recommendations,
  productTypeSlug,
  onReset,
}: InvalidImageResultsProps) {
  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <UnrecognizedPhotoCard invalidReason={invalidReason} />
      {recommendations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle size="lg">AI recommendations</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc space-y-2 pl-5 text-sm">
              {recommendations.map((recommendation) => (
                <li key={recommendation}>{recommendation}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
      <div className="flex flex-col items-center gap-3">
        <Button variant="primary" onClick={onReset}>
          <RotateCcw className="h-4 w-4" />
          Try again with new photos
        </Button>
        <Link
          href={`/price-index/${productTypeSlug}`}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-action"
        >
          Or browse market pricing
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}

function UnrecognizedPhotoCard({ invalidReason }: { invalidReason?: string }) {
  return (
    <Card className="border-warning/20 bg-warning-bg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-warning-strong">
          <ImageOff className="h-5 w-5" />
          We could not grade this photo
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm text-warning-strong">
        <p>
          {invalidReason ??
            "We couldn't find recognizable packaging in the uploaded photos."}
        </p>
        <p className="font-medium">For best results:</p>
        <ul className="space-y-1.5">
          {PHOTO_TIPS.map((tip) => (
            <li key={tip} className="flex items-start gap-2">
              <Camera className="mt-0.5 h-4 w-4 shrink-0" />
              {tip}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
